import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import StaffTab from '../components/employees/StaffTab';
import TimesheetTab from '../components/employees/TimesheetTab';
import PayoutSheet from '../components/employees/PayoutSheet';
import ScheduleTab from '../components/employees/ScheduleTab';
import PayrollAnalytics from '../components/employees/PayrollAnalytics';
import PayslipModal from '../components/employees/PayslipModal';
import TimesheetImportModal from '../components/employees/TimesheetImportModal';
import { monthLabel, shiftMonth, plural } from '../components/employees/tsFormat';
import {
  getHrEmployees, getHrPeriods, getHrPeriod, createHrPeriod, syncHrPeriod, setHrPeriodStatus,
  saveHrDays, setHrClosedDay, addHrPeriodEntry, deleteHrEntry, updateHrEntry, resetHrEntryTotal,
  payrollExportUrl,
} from '../services/api';
import './EmployeesPage.css';
import './Timesheet.css';

const TABS = [
  { key: 'timesheet', label: 'Табель' },
  { key: 'payout', label: 'Ведомость' },
  { key: 'staff', label: 'Сотрудники' },
  { key: 'schedule', label: 'Расписание' },
  { key: 'analytics', label: 'Аналитика ФОТ' },
];

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <path d="M15 6l-6 6 6 6" /> : <path d="M9 6l6 6-6 6" />}
  </svg>
);

/* Ответ сервера — плоский список отметок; сетке удобнее days[entry_id][day]. */
const indexDays = (rows = []) => {
  const map = {};
  rows.forEach(row => {
    if (!map[row.entry_id]) map[row.entry_id] = {};
    map[row.entry_id][row.day] = row;
  });
  return map;
};

const readTab = () => {
  const fromUrl = new URLSearchParams(window.location.search).get('tab');
  return TABS.some(t => t.key === fromUrl) ? fromUrl : 'timesheet';
};

const EmployeesPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTabState] = useState(readTab);
  const [staff, setStaff] = useState({
    employees: [], positions: [], job_positions: [], accounts: [], last_month: null, iiko_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [month, setMonth] = useState(null);       // {period, entries, summary, days, days_in_month}
  const [monthLoading, setMonthLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveQueue = useRef(Promise.resolve());
  const pendingSaves = useRef(0);

  const setTab = (key) => {
    setTabState(key);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', key);
    window.history.replaceState(null, '', url);
  };

  const loadStaff = useCallback(async () => {
    try {
      const data = await getHrEmployees();
      setStaff({
        employees: data.employees || [], positions: data.positions || [],
        job_positions: data.job_positions || [], accounts: data.accounts || [],
        last_month: data.last_month || null, iiko_enabled: !!data.iiko_enabled,
      });
      setError(null);
    } catch (e) {
      setError(e.response?.status === 403
        ? 'Раздел доступен владельцу и управляющему'
        : 'Не удалось загрузить сотрудников — обновите страницу');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPeriods = useCallback(async (preferId) => {
    const data = await getHrPeriods();
    const list = data.periods || [];
    setPeriods(list);
    setPeriodId(prev => {
      if (preferId && list.some(p => p.id === preferId)) return preferId;
      if (prev && list.some(p => p.id === prev)) return prev;
      // По умолчанию — открытый месяц (его заполняют), иначе самый свежий.
      const open = list.find(p => p.status === 'draft');
      return (open || list[0] || {}).id || null;
    });
    return list;
  }, []);

  const loadMonth = useCallback(async (id) => {
    if (!id) { setMonth(null); return; }
    setMonthLoading(true);
    try {
      const data = await getHrPeriod(id);
      setMonth({ ...data, days: indexDays(data.days) });
    } catch (e) {
      setNotice({ type: 'error', text: 'Не удалось загрузить месяц — обновите страницу' });
    } finally {
      setMonthLoading(false);
    }
  }, []);

  useEffect(() => { loadStaff(); loadPeriods().catch(() => {}); }, [loadStaff, loadPeriods]);
  useEffect(() => { loadMonth(periodId); }, [periodId, loadMonth]);

  const period = month?.period;
  const closed = period?.status === 'closed';
  const index = periods.findIndex(p => p.id === periodId);
  const older = index >= 0 ? periods[index + 1] : null;
  const newer = index > 0 ? periods[index - 1] : null;
  const nextMonth = period ? shiftMonth(period, 1) : null;

  const now = new Date();
  const todayDay = period && period.year === now.getFullYear() && period.month === now.getMonth() + 1
    ? now.getDate() : null;

  const fail = (e, fallback) => setNotice({ type: 'error', text: e.response?.data?.error || fallback });

  const replaceEntries = (list) => setMonth(m => {
    if (!m) return m;
    const byId = Object.fromEntries(list.map(e => [e.id, e]));
    return { ...m, entries: m.entries.map(e => byId[e.id] || e) };
  });

  /* Клетки сначала меняются на экране, потом уходят на сервер: при наборе
     табеля задержка в каждой клетке раздражает сильнее всего. Если сервер
     отказал — показываем причину и перечитываем месяц. */
  const saveCells = useCallback(async (items) => {
    if (!periodId) return;
    setMonth(m => {
      if (!m) return m;
      const days = { ...m.days };
      items.forEach(({ entry_id: id, day, value }) => {
        const row = { ...(days[id] || {}) };
        const text = String(value).trim().toLowerCase();
        const n = Number(text.replace(',', '.'));
        if (text === '' || n === 0) delete row[day];
        else if (!Number.isNaN(n)) row[day] = { day, entry_id: id, hours: n, day_type: 'work' };
        else row[day] = { day, entry_id: id, hours: 0, day_type: 'other', mark: text };
        days[id] = row;
      });
      return { ...m, days };
    });
    /* Сохранения идут строго по очереди: каждый ответ несёт полный снимок
       дней месяца, и если ответ раннего запроса придёт позже позднего, он
       затрёт на экране только что набранное. */
    const run = async () => {
      try {
        const res = await saveHrDays(periodId, items);
        pendingSaves.current -= 1;
        // Пока в очереди есть ещё сохранения, снимок дней не применяем —
        // он не знает о клетках, которые уже набраны, но ещё не отправлены.
        if (pendingSaves.current === 0) {
          setMonth(m => (m ? { ...m, days: indexDays(res.days) } : m));
        }
        setMonth(m => (m ? { ...m, summary: res.summary } : m));
        replaceEntries(res.entries || []);
      } catch (e) {
        pendingSaves.current -= 1;
        fail(e, 'Не удалось сохранить клетки');
        loadMonth(periodId);
      }
    };
    pendingSaves.current += 1;
    saveQueue.current = saveQueue.current.then(run);
  }, [periodId, loadMonth]);

  const saveEntry = useCallback(async (entryId, field, value) => {
    try {
      const res = await updateHrEntry(entryId, { [field]: value });
      replaceEntries([res.entry]);
      setMonth(m => (m ? { ...m, summary: res.summary } : m));
    } catch (e) {
      fail(e, 'Не удалось сохранить сумму');
    }
  }, []);

  const resetHalf = useCallback(async (entryId, half) => {
    try {
      const res = await resetHrEntryTotal(entryId, half);
      replaceEntries([res.entry]);
      setMonth(m => (m ? { ...m, summary: res.summary } : m));
    } catch (e) {
      fail(e, 'Не удалось вернуть расчёт');
    }
  }, []);

  const closedDay = useCallback(async (day, note, isClosed) => {
    try {
      const res = await setHrClosedDay(periodId, day, note, isClosed);
      setMonth(m => (m ? { ...m, period: res.period } : m));
    } catch (e) {
      fail(e, 'Не удалось отметить день');
    }
  }, [periodId]);

  const addEntry = async (form) => {
    try {
      await addHrPeriodEntry(periodId, form);
      await loadMonth(periodId);
      return null;
    } catch (e) {
      return e.response?.data?.error || 'Не удалось добавить строку';
    }
  };

  const sync = async () => {
    try {
      const res = await syncHrPeriod(periodId);
      await loadMonth(periodId);
      setNotice({ type: 'ok', text: res.added ? `Добавлено в табель: ${res.added}` : 'Все работающие уже в табеле' });
    } catch (e) {
      fail(e, 'Не удалось обновить состав');
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Убрать «${entry.full_name}» (${entry.position}) из табеля за ${period.label.toLowerCase()}? Отметки по дням этой строки удалятся.`)) return;
    try {
      await deleteHrEntry(entry.id);
      setPayslip(null);
      await loadMonth(periodId);
    } catch (e) {
      fail(e, 'Не удалось убрать строку');
    }
  };

  const startMonth = async ({ year, month: m }) => {
    setBusy(true);
    try {
      const res = await createHrPeriod(year, m);
      await loadPeriods(res.period.id);
      setNotice({ type: 'ok', text: `${res.period.label}: в табель добавлено ${res.added} ${plural(res.added, 'человек', 'человека', 'человек')}` });
    } catch (e) {
      fail(e, 'Не удалось начать месяц');
    } finally {
      setBusy(false);
    }
  };

  const toggleClosed = async () => {
    const next = closed ? 'draft' : 'closed';
    if (next === 'closed' && !window.confirm(`Закрыть ${period.label.toLowerCase()}? Табель и суммы станут только для чтения.`)) return;
    setBusy(true);
    try {
      const res = await setHrPeriodStatus(periodId, next);
      setMonth(m => ({ ...m, period: res.period }));
      setPeriods(list => list.map(p => (p.id === res.period.id ? res.period : p)));
    } catch (e) {
      fail(e, 'Не удалось изменить статус месяца');
    } finally {
      setBusy(false);
    }
  };

  const afterImport = async (result) => {
    setImportOpen(false);
    await loadStaff();
    const list = await loadPeriods();
    const target = result?.last_period;
    const found = target && list.find(p => p.year === target.year && p.month === target.month);
    if (found) setPeriodId(found.id);
    else loadMonth(periodId);
  };

  const activeCount = useMemo(() => staff.employees.filter(e => e.status === 'active').length, [staff.employees]);
  const monthTabs = tab === 'timesheet' || tab === 'payout';
  const pageClass = ['hr-page', 'ts-page', tab === 'timesheet' ? 'ts-wide hr-fixed' : '', tab === 'schedule' ? 'hr-fixed' : '']
    .filter(Boolean).join(' ');

  if (!isAuthenticated) return null;

  const renderMonthBody = () => {
    if (!periods.length) {
      const current = { year: now.getFullYear(), month: now.getMonth() + 1 };
      return (
        <div className="ts-empty-block">
          <strong>Табеля пока нет</strong>
          <span>
            Загрузите Excel-табель — подтянутся все месяцы, люди, должности и ставки. Или начните
            {' '}{monthLabel(current).toLowerCase()} с нуля: в него попадут все работающие из справочника.
          </span>
          <div className="ts-empty-actions">
            <button type="button" className="ui-btn-primary" onClick={() => setImportOpen(true)}>Загрузить табель из Excel</button>
            <button type="button" className="ui-btn-ghost" disabled={busy} onClick={() => startMonth(current)}>
              Начать {monthLabel(current).toLowerCase()}
            </button>
          </div>
        </div>
      );
    }
    if (!month || month.period.id !== periodId) {
      return <div className="ts-empty-block">{monthLoading ? 'Загружаю табель…' : ''}</div>;
    }
    if (tab === 'payout') return <PayoutSheet data={month} />;
    return (
      <TimesheetTab
        data={month}
        closed={closed}
        todayDay={todayDay}
        employees={staff.employees}
        jobPositions={staff.job_positions}
        onSaveCells={saveCells}
        onSaveEntry={saveEntry}
        onResetHalf={resetHalf}
        onOpenEntry={setPayslip}
        onClosedDay={closedDay}
        onAddEntry={addEntry}
        onSync={sync}
      />
    );
  };

  return (
    <div className={pageClass}>
      <Navbar user={user} isAdmin={isAdmin} onLogout={logout} />

      <div className="hr-shell ts-shell">
        <header className="ts-head">
          <div className="ts-head-title">
            <h1>Сотрудники</h1>
            <p>
              {loading ? 'Загрузка…' : `${activeCount} ${plural(activeCount, 'человек работает', 'человека работают', 'человек работают')}`}
              {periods.length > 0 && !loading && `, табель с ${monthLabel(periods[periods.length - 1]).toLowerCase()}`}
            </p>
          </div>

          {monthTabs && period && (
            <div className="ts-month" aria-label="Месяц табеля">
              <button type="button" className="ts-month-arrow" disabled={!older}
                onClick={() => older && setPeriodId(older.id)} aria-label="Предыдущий месяц">
                <Chevron dir="left" />
              </button>
              <div className="ts-month-label">
                <strong>{period.label}</strong>
                <span className={`ts-status ${closed ? 'is-closed' : 'is-open'}`}>{closed ? 'закрыт' : 'открыт'}</span>
              </div>
              {newer ? (
                <button type="button" className="ts-month-arrow" onClick={() => setPeriodId(newer.id)} aria-label="Следующий месяц">
                  <Chevron dir="right" />
                </button>
              ) : (
                <button type="button" className="ts-month-next" disabled={busy} onClick={() => startMonth(nextMonth)}
                  title="Создать следующий месяц: в него попадут все работающие">
                  Начать {monthLabel(nextMonth).split(' ')[0].toLowerCase()}
                </button>
              )}
            </div>
          )}

          {monthTabs && period && (
            <div className="ts-head-actions">
              <a className="ui-btn-ghost" href={payrollExportUrl(periodId)}>Выгрузить в Excel</a>
              {isAdmin && (
                <button type="button" className={closed ? 'ui-btn-primary' : 'ui-btn-ghost'} onClick={toggleClosed} disabled={busy}>
                  {closed ? 'Открыть месяц' : 'Закрыть месяц'}
                </button>
              )}
            </div>
          )}
        </header>

        <nav className="ui-tabs ts-tabs" aria-label="Раздел">
          {TABS.map(t => (
            <button key={t.key} type="button" className={`ui-tab ${tab === t.key ? 'active' : ''}`}
              aria-current={tab === t.key ? 'page' : undefined} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>

        {notice && (
          <div className={`hr-notice ${notice.type === 'error' ? 'error' : ''}`} role="status">
            <span>{notice.text}</span>
            <button className="hr-notice-close" onClick={() => setNotice(null)} aria-label="Скрыть">×</button>
          </div>
        )}

        {monthTabs && closed && (
          <div className="ts-locked" role="note">
            {period.label} закрыт — табель только для чтения.
            {isAdmin ? ' Чтобы поправить, нажмите «Открыть месяц».' : ' Открыть его может владелец.'}
          </div>
        )}

        {error ? (
          <div className="ts-empty-block"><strong>{error}</strong></div>
        ) : (
          <>
            {monthTabs && renderMonthBody()}
            {tab === 'staff' && (
              <StaffTab
                employees={staff.employees}
                positions={staff.positions}
                jobPositions={staff.job_positions}
                onJobPositionsChanged={(next) => setStaff(s => ({ ...s, job_positions: next }))}
                accounts={staff.accounts}
                lastMonth={staff.last_month}
                loading={loading}
                isAdmin={isAdmin}
                onChanged={loadStaff}
                onImported={afterImport}
              />
            )}
            {tab === 'schedule' && <ScheduleTab employees={staff.employees} />}
            {tab === 'analytics' && <PayrollAnalytics iikoEnabled={staff.iiko_enabled} />}
          </>
        )}
      </div>

      {payslip && (
        <PayslipModal
          entryId={payslip.id}
          onClose={() => setPayslip(null)}
          onRemove={!closed ? () => removeEntry(payslip) : null}
        />
      )}

      {importOpen && (
        <TimesheetImportModal isAdmin={isAdmin} onClose={() => setImportOpen(false)} onImported={afterImport} />
      )}
    </div>
  );
};

export default EmployeesPage;
